import { useEffect, useMemo } from 'react';
import { Avatar, Button, Drawer, Form as AntForm, Input, Space } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { AtSign, Link2, Linkedin, Twitter, User } from 'lucide-react';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { ImageField } from '@shared/components/MediaUpload';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { authorsApi } from '@api/authors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';
import type { Author } from '../types';

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    displayName: z.string().min(2, t('validation.displayNameMin')).max(80),
    slug: z
      .string()
      .min(2)
      .max(80)
      .regex(SLUG_RULE, t('validation.slug')),
    email: z.string().email(t('validation.email')).or(z.literal('')).optional(),
    title: z.string().max(80).optional(),
    bio: z.string().max(500).optional(),
    avatarUrl: z.string().max(500).nullable().optional(),
    twitter: z.string().max(40).optional(),
    linkedin: z.string().max(80).optional(),
    website: z.string().url(t('validation.website')).or(z.literal('')).optional(),
  });
}
type Values = z.infer<ReturnType<typeof makeSchema>>;

interface Props {
  open: boolean;
  author: Author | null;
  onClose: () => void;
}

function emptyToUndef(v?: string | null): string | undefined {
  return v && v.trim() ? v.trim() : undefined;
}

export function AuthorDrawer({ open, author, onClose }: Props) {
  const { t } = useTranslation(['authors', 'common', 'validation', 'errors']);
  const isEdit = !!author;
  const qc = useQueryClient();

  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);

  const initialValues = useMemo<Values>(
    () => ({
      displayName: author?.displayName ?? '',
      slug: author?.slug ?? '',
      email: author?.email ?? '',
      title: author?.title ?? '',
      bio: author?.bio ?? '',
      avatarUrl: author?.avatarUrl ?? '',
      twitter: author?.twitter ?? '',
      linkedin: author?.linkedin ?? '',
      website: author?.website ?? '',
    }),
    [author],
  );

  const createMutation = useMutation({
    mutationFn: (v: Values) =>
      authorsApi.create({
        displayName: v.displayName,
        slug: v.slug,
        email: emptyToUndef(v.email),
        title: emptyToUndef(v.title),
        bio: emptyToUndef(v.bio),
        avatarUrl: emptyToUndef(v.avatarUrl),
        twitter: emptyToUndef(v.twitter),
        linkedin: emptyToUndef(v.linkedin),
        website: emptyToUndef(v.website),
      }),
    onSuccess: (a) => {
      toast.success(t('toast.created', { name: a.displayName }));
      void recordAuditAction({
        action: 'author.create',
        resource: 'author',
        resourceId: a.id,
        summary: t('audit.created', { name: a.displayName }),
      });
      void qc.invalidateQueries({ queryKey: ['authors'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (v: Values) => {
      if (!author) throw new Error('no author');
      // avatarUrl: send explicit `null` when the user cleared the image so the
      // API client doesn't silently fall back to the existing value. Other
      // free-text fields keep emptyToUndef (the BE treats null as "leave alone"
      // there, and the drawer doesn't have a dedicated "clear" gesture for them).
      return authorsApi.update(author.id, {
        displayName: v.displayName,
        slug: v.slug,
        email: emptyToUndef(v.email),
        title: emptyToUndef(v.title),
        bio: emptyToUndef(v.bio),
        avatarUrl: v.avatarUrl?.trim() ? v.avatarUrl.trim() : null,
        twitter: emptyToUndef(v.twitter),
        linkedin: emptyToUndef(v.linkedin),
        website: emptyToUndef(v.website),
      });
    },
    onSuccess: (a) => {
      toast.success(t('toast.updated', { name: a.displayName }));
      void recordAuditAction({
        action: 'author.update',
        resource: 'author',
        resourceId: a.id,
        summary: t('audit.updated', { name: a.displayName }),
      });
      void qc.invalidateQueries({ queryKey: ['authors'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  useEffect(() => {
    if (!open) {
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      title={isEdit ? t('drawer.editTitle', { name: author?.displayName }) : t('drawer.createTitle')}
      open={open}
      onClose={onClose}
      width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth : 560)}
      destroyOnClose
    >
      <Form
        schema={schema}
        defaultValues={initialValues}
        onSubmit={(v) => (isEdit ? updateMutation.mutate(v) : createMutation.mutate(v))}
      >
        {({ watch, setValue }) => {
          const displayName = watch('displayName');
          const slug = watch('slug');
          const avatarUrl = watch('avatarUrl');
          const initials = (displayName || '')
            .split(' ')
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
          return (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  marginBottom: 16,
                  background: 'var(--ds-surface-2)',
                  borderRadius: 8,
                }}
              >
                <Avatar
                  size={56}
                  src={avatarUrl ? resolveAssetUrl(avatarUrl) : undefined}
                  style={{
                    background: 'var(--ds-brand)',
                    color: 'var(--ds-text-on-brand)',
                    fontWeight: 600,
                  }}
                >
                  {initials || <User size={20} />}
                </Avatar>
                <div style={{ lineHeight: 1.2 }}>
                  <div style={{ fontWeight: 600 }}>{displayName || t('preview.defaultName')}</div>
                  <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                    {t('preview.slugPath', { slug: slug || t('preview.defaultSlug') })}
                  </div>
                </div>
              </div>
              <AntForm layout="vertical" component="div">
                <FormField name="displayName" label={t('form.labels.displayName')} required>
                  <Input size="large" placeholder={t('form.placeholders.displayName')} />
                </FormField>
                <AntForm.Item label={t('form.labels.slug')} required>
                  <SlugInput
                    value={slug}
                    onChange={(v) => setValue('slug', v, { shouldDirty: true, shouldValidate: true })}
                    sourceValue={displayName}
                  />
                </AntForm.Item>
                <FormField name="email" label={t('form.labels.email')}>
                  <Input size="large" prefix={<AtSign size={14} />} placeholder={t('form.placeholders.email')} />
                </FormField>
                <FormField name="title" label={t('form.labels.title')}>
                  <Input placeholder={t('form.placeholders.title')} />
                </FormField>
                <FormField name="bio" label={t('form.labels.bio')} help={t('form.help.bio')}>
                  <Input.TextArea rows={3} maxLength={500} showCount />
                </FormField>
                <FormField name="avatarUrl" label={t('form.labels.avatar')}>
                  <ImageField label={t('form.avatar.label')} maxSizeMB={2} hint={t('form.avatar.hint')} />
                </FormField>
                <FormField name="twitter" label={t('form.labels.twitter')}>
                  <Input prefix={<Twitter size={14} />} placeholder={t('form.placeholders.twitter')} />
                </FormField>
                <FormField name="linkedin" label={t('form.labels.linkedin')}>
                  <Input prefix={<Linkedin size={14} />} placeholder={t('form.placeholders.linkedin')} />
                </FormField>
                <FormField name="website" label={t('form.labels.website')}>
                  <Input prefix={<Link2 size={14} />} placeholder={t('form.placeholders.website')} />
                </FormField>
              </AntForm>
              <Space style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {isEdit ? t('form.submit.save') : t('form.submit.create')}
                </Button>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
              </Space>
            </>
          );
        }}
      </Form>
    </Drawer>
  );
}
